import { useMemo } from 'react';

import {
  EDIT_ENTITY_SECTION_DISPLAY,
  VIEW_ENTITY_SECTION_DISPLAY,
} from '../EditEntitySplitScreen/EditEntitySplitScreen.constants';
import { EditEntitySection } from '../EditEntitySplitScreen/EditEntitySplitScreen.types';
import { getInitialSectionOptions } from '../EditEntitySplitScreen/EditEntitySplitScreen.utils';
import { EditEntitySplitScreenOpenModalButtonProps } from '../EditEntitySplitScreen/EditEntitySplitScreenOpenModalButton';
import {
  isCurrentBeneficiaryEntityKind,
  REMAINDER_BENEFICIARY_ENTITY_KINDS,
} from '../EntityBeneficiariesSubform/EntityBeneficiariesSubform.constants';
import { EntityType } from '../types/EntityType';
import { getEntityKindFromEntityType } from '../utils/getEntityKindFromEntityType';
import { EntityDetailsView } from './EntityDetails';

export type useOpenEntityModalButtonPropsOutput =
  | Pick<
      EditEntitySplitScreenOpenModalButtonProps,
      | 'initialSectionOptions'
      | 'initialSection'
      | 'fullWidth'
      | 'buttonText'
      | 'size'
    >
  | undefined;

export function useOpenEntityModalButtonProps(
  activeTab: EntityDetailsView | '',
  entityType: EntityType | null,
  canEditHouseholdData: boolean
): useOpenEntityModalButtonPropsOutput | undefined {
  return useMemo<useOpenEntityModalButtonPropsOutput | undefined>(() => {
    const initialSectionOptions = getInitialSectionOptions(
      activeTab,
      entityType
    );

    if (!initialSectionOptions) return undefined;

    if (initialSectionOptions.length === 1) {
      // There's only 1 section option, so we won't show the button as a dropdown.
      const firstOption: EditEntitySection = initialSectionOptions[0]!;
      const buttonText = getButtonText(
        firstOption,
        entityType,
        canEditHouseholdData
      );

      return {
        buttonText,
        initialSection: firstOption,
        fullWidth: true,
        size: 'sm' as const,
      };
    }

    // Show the button as a dropdown to user can select which section to open to.
    return {
      initialSectionOptions,
    };
  }, [activeTab, canEditHouseholdData, entityType]);
}

function getButtonText(
  section: EditEntitySection,
  entityType: EntityType | null,
  canEditHouseholdData: boolean
): string {
  if (
    entityType &&
    EDIT_ENTITY_SECTION_BENEFICIARIES.includes(section) &&
    canEditHouseholdData
  ) {
    const entityKind = getEntityKindFromEntityType(entityType);

    if (isCurrentBeneficiaryEntityKind(entityKind)) {
      return 'Edit current beneficiaries';
    } else if (REMAINDER_BENEFICIARY_ENTITY_KINDS.includes(entityKind)) {
      return 'Edit remainder beneficiaries';
    }
  }

  return canEditHouseholdData
    ? EDIT_ENTITY_SECTION_DISPLAY[section]
    : VIEW_ENTITY_SECTION_DISPLAY[section];
}

const EDIT_ENTITY_SECTION_BENEFICIARIES: EditEntitySection[] = [
  EditEntitySection.BENEFICIARIES,
  EditEntitySection.LIFETIME_BENEFICIARIES,
  EditEntitySection.REMAINDER_BENEFICIARIES,
  EditEntitySection.INCOME_BENEFICIARIES,
  EditEntitySection.LEAD_BENEFICIARIES,
];
